import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/auth.img.png';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    setSuccess(true);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.875rem',
      height: '43px',
      gap: '24px',
      opacity: 1,
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(217, 217, 217, 1) !important', borderWidth: '1px !important' },
      '& input': { padding: '10px 10px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 4, sm: 6, md: 7 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 3, md: 3 },
          backgroundColor: '#ffffff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: 80, objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {success ? (
          /* ── Success state ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mt: 4 }}>
            <CheckCircleOutline sx={{ fontSize: 64, color: 'rgba(43, 103, 176, 1)', mb: 2 }} />
            <Typography
              sx={{
                fontFamily: '"Unbounded", sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '40px',
                color: 'rgba(0, 0, 0, 1)',
                opacity: 0.8,
                mb: 1.5,
              }}
            >
              Password Reset!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '140%',
                color: 'rgba(87, 81, 81, 1)',
                mb: 4,
              }}
            >
              Your password has been successfully reset. You can now log in with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                height: '43px',
                backgroundColor: 'rgba(43, 103, 176, 1)',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textTransform: 'none',
                letterSpacing: 0.3,
                boxShadow: 'none',
                '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
              }}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <>
            {/* Heading */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: '33.31px',
                  lineHeight: '48px',
                  letterSpacing: '0%',
                  color: 'rgba(0, 0, 0, 1)',
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: 'rgba(87, 81, 81, 1)',
                }}
              >
                Create a strong new password for your account.
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

              {/* New Password */}
              <Typography sx={labelSx}>New Password</Typography>
              <TextField
                fullWidth
                placeholder="Enter new password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setError(''); }}
                disabled={loading}
                size="small"
                sx={{ ...inputSx, mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password */}
              <Typography sx={labelSx}>Confirm Password</Typography>
              <TextField
                fullWidth
                placeholder="Confirm new password"
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); setError(''); }}
                disabled={loading}
                size="small"
                sx={{ ...inputSx, mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Submit button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  height: '43px',
                  backgroundColor: 'rgba(43, 103, 176, 1)',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  letterSpacing: 0.3,
                  boxShadow: 'none',
                  mb: 2.5,
                  '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
                  '&:disabled': { backgroundColor: '#93C5FD' },
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    Resetting...
                  </Box>
                ) : 'Reset Password'}
              </Button>

              {/* Back to login */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/')}
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: 'rgba(43, 103, 176, 1)',
                  }}
                >
                  <ArrowBack sx={{ fontSize: '16px' }} />
                  Back to Login
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '55%',
          height: '100%',
          backgroundColor: '#0A0F1E',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt="PetroTag360 dashboard preview"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
          }}
        />
      </Box>

    </Box>
  );
};

export default ResetPasswordPage;
